import React from "react";
import { Neo, NeoGrid } from "@singularsystems/neo-react";
import { observer } from "mobx-react";
import UpdateSecurityTransactionComponentVM from "./UpdateSecurityTransactionComponentVM";
import { TextConstants } from "../../../Common/TextConstants";
import { NumberFormat } from "@singularsystems/neo-core/dist/NumberUtils";
import InfoLabelHover from "../../../Common/Components/InfoLabelHover/InfoLabelHover";
import { Validation } from "@singularsystems/neo-core";
import RawGenericTransactionLookup from "../Models/Lookups/RawGenericTransactionLookup";
import CurrencyComponent from "../../../Common/Components/Currency/CurrencyComponent";
import { AuthorisableRequests } from "../../../AccessControl/Models/Commands/Security/Roles";

interface IUpdateSecurityTransactionComponentProps {
  viewModel: UpdateSecurityTransactionComponentVM;
  onBack: () => void;
  onSave: () => void;
  onAuthorisation: () => void;
  isBulkUpdate?: boolean;
  bulkSelectedCount?: number;
}

@observer
export default class UpdateSecurityTransactionComponent extends React.Component<IUpdateSecurityTransactionComponentProps> {
  public render() {
    var viewModel = this.props.viewModel;

    return (
      <>
        <Neo.Loader task={viewModel.taskRunner}>
          <Neo.Card title={TextConstants.Titles.UpdateTransactions}>
            <Neo.Form
              model={viewModel.updateTransactionCommand}
              validationDisplayMode={Validation.DisplayMode.AfterSubmit}
            >
              <Neo.GridLayout md={4}>
                <Neo.FormGroup
                  label={TextConstants.Labels.NewEventMapping}
                  bindContext={
                    viewModel.updateTransactionCommand.meta.newStaticEventID
                  }
                >
                  <Neo.AutoCompleteDropDown
                    minCharacters={3}
                    delayMs={1500}
                    bind={viewModel.updateTransactionCommand.meta.newStaticEventID}
                    bindDisplay={viewModel.updateTransactionCommand.meta.newEventName}
                    displayMember={"staticEventName"}
                    items={viewModel.filteredEvents}
                    itemSource={async (searchText) => viewModel.events.filter(event => event.staticEventName?.toLowerCase().includes(searchText.toLowerCase()))}
                  />
                </Neo.FormGroup>

                {viewModel.currencyComponentVM.staticCurrencyLookupList.length >
                  0 && (
                    <CurrencyComponent
                      viewModel={viewModel.currencyComponentVM}
                      label={TextConstants.Labels.InstrumentCurrency}
                    />
                  )}

                <Neo.FormGroup
                  label={TextConstants.Labels.NewInstrumentMapping}
                  bindContext={
                    viewModel.updateTransactionCommand.meta
                      .newStaticInstrumentID
                  }
                >
                  <Neo.AutoCompleteDropDown
                    minCharacters={3}
                    delayMs={1500}
                    bind={viewModel.updateTransactionCommand.meta.newStaticInstrumentID}
                    bindDisplay={viewModel.updateTransactionCommand.meta.newInstrumentDescription}
                    displayMember={"description"}
                    items={viewModel.filteredInstruments}
                    itemSource={async (searchText) => viewModel.instruments.filter(instrument => instrument.description?.toLowerCase().includes(searchText.toLowerCase()))}
                  />
                </Neo.FormGroup>
                <Neo.FormGroup
                  label={
                    <InfoLabelHover
                      title={TextConstants.Labels.Price}
                      content={TextConstants.Tooltips.PriceUpdateTooltip}
                    />
                  }
                  bind={viewModel.updateTransactionCommand.meta.priceOverride}
                  isReadOnly={
                    viewModel.updateTransactionCommand.updatePriceToBookCostInd
                  }
                  numProps={{ format: NumberFormat.Decimals }}
                />
                <Neo.FormGroup
                  label={TextConstants.Labels.TransactionTime}
                  bind={viewModel.updateTransactionCommand.meta.newTime}
                  dateProps={{ formatString: "HH:mm:ss" }}
                />
                <Neo.FormGroup
                  appendText="fa-calendar"
                  label={TextConstants.Labels.DateEvent}
                  bind={viewModel.updateTransactionCommand.meta.newDateEvent}
                />
              </Neo.GridLayout>
              <div className="row">
                <div className="col-lg-1">
                  <Neo.FormGroup
                    className="pt-1"
                    label={viewModel.updateTransactionCommand.meta.isExcludedInd.value === null
                      ? TextConstants.Labels.ExcludeValueUnchanged
                      : viewModel.updateTransactionCommand.meta.isExcludedInd.value
                        ? TextConstants.Labels.ExcludeAll
                        : TextConstants.Labels.IncludeAll}
                    bind={viewModel.updateTransactionCommand.meta.isExcludedInd}
                  />
                </div>
                <div className="col-lg-1">
                  <Neo.FormGroup
                    className="pt-1"
                    label={TextConstants.Labels.UpdatePriceToBookCost}
                    bind={
                      viewModel.updateTransactionCommand.meta
                        .updatePriceToBookCostInd
                    }
                    input={{ type: "switch" }}
                    isReadOnly={
                      viewModel.updateTransactionCommand.meta.priceOverride
                        .value === undefined
                        ? true
                        : false
                    }
                  />
                </div>
                <div className="col-lg-1">
                  <Neo.FormGroup
                    className="pt-1"
                    label={TextConstants.Labels.UnassignClientMapping}
                    bind={
                      viewModel.updateTransactionCommand.meta
                        .setClientAccountIDToNull
                    }
                    input={{ type: "switch" }}
                  />
                </div>
                <div className="col-lg-1">
                  <Neo.FormGroup
                    className="pt-1"
                    label={TextConstants.Labels.Reversal}
                    bind={viewModel.updateTransactionCommand.meta.isReversalInd}
                    input={{ type: "switch" }}
                  />
                </div>
              </div>
              <div className="row">
                <div className="pl-3">
                  <Neo.Button variant="primary"
                    onClick={() => this.props.onSave()}
                  >
                    {TextConstants.Buttons.Save}
                  </Neo.Button>
                  &nbsp;
                </div>
                {viewModel.authorisationService.hasRole(AuthorisableRequests.Edit) &&
                  <div>
                    <Neo.Button
                      variant="secondary"
                      onClick={() => this.props.onAuthorisation()}
                    >
                      {TextConstants.Buttons.RequestAuthorisation}
                    </Neo.Button>
                  </div>
                }
                &nbsp;
                <div>
                  <Neo.Button
                    onClick={() => this.props.onBack()}
                    variant="secondary"
                  >
                    {TextConstants.Buttons.Cancel}
                  </Neo.Button>
                </div>
              </div>
              {this.props.isBulkUpdate &&
                <Neo.Alert variant="warning">
                  {TextConstants.GeneralText.BulkUpdatePreview.replace("{bulkSelectedNumber}", String(this.props.bulkSelectedCount ?? 0))}
                </Neo.Alert>
              }
            </Neo.Form>

            <Neo.Card>
              <div className="inner-container">
                <NeoGrid.Grid<RawGenericTransactionLookup>
                  items={viewModel.selectedSecurityTransactions}
                  className={"table-striped"}
                >
                  {(data, meta) => (
                    <NeoGrid.Row>
                      <NeoGrid.Column
                        display={meta.eventDate}
                        label={TextConstants.Labels.EventDate}
                      />
                      <NeoGrid.Column
                        display={meta.eventTime}
                        label={TextConstants.Labels.EventTime}
                      />
                      <NeoGrid.Column
                        display={meta.clientNumber}
                        label={TextConstants.Labels.ClientNumber}
                      />
                      <NeoGrid.Column
                        display={meta.stockbrokerAccount}
                        label={TextConstants.Labels.BrokerAccount}
                      />
                      <NeoGrid.Column
                        display={meta.eventName}
                        label={TextConstants.Labels.EventName}
                      />
                      <NeoGrid.Column display={meta.buyOrSell} />
                      <NeoGrid.Column
                        display={meta.eventID}
                        label={TextConstants.Labels.EventID}
                      />
                      <NeoGrid.Column
                        display={meta.relatedEventID}
                        label={TextConstants.Labels.RelatedTrasactionID}
                      />
                      <NeoGrid.Column
                        display={meta.instrumentDescription}
                        label={TextConstants.Labels.InstrumentDescription}
                      />
                      <NeoGrid.Column
                        display={meta.ticker}
                        label={TextConstants.Labels.TickerSymbol}
                      />
                      <NeoGrid.Column
                        display={meta.isin}
                        label={TextConstants.Labels.Isin}
                      />
                      <NeoGrid.Column
                        display={meta.currency}
                        label={TextConstants.Labels.InstrumentCurrency}
                      />
                      <NeoGrid.Column
                        display={meta.marketPrice}
                        label={
                          <InfoLabelHover
                            title={TextConstants.Labels.MarketPrice}
                            content={TextConstants.Tooltips.MarketValueTooltip}
                          />
                        }
                        cellClassName="text-right"
                      />
                      <NeoGrid.Column
                        display={meta.unitPrice}
                        label={
                          <InfoLabelHover
                            title={TextConstants.Labels.UnitPrice}
                            content={TextConstants.Tooltips.UnitPriceTooltip}
                          />
                        }
                        cellClassName="text-right"
                      />
                      <NeoGrid.Column
                        display={meta.quantity}
                        label={TextConstants.Labels.Quantity}
                        cellClassName="text-right"
                      />
                      <NeoGrid.Column
                        display={meta.amount}
                        label={
                          <InfoLabelHover
                            title={TextConstants.Labels.Amount}
                            content={TextConstants.Tooltips.AmountTooltip}
                          />
                        }
                        cellClassName="text-right"
                      />
                      <NeoGrid.Column
                        display={meta.amountUSD}
                        label={
                          <InfoLabelHover
                            title={TextConstants.Labels.AmountNC}
                            content={TextConstants.Tooltips.AmountUSD}
                          />
                        }
                        cellClassName="text-right"
                      />
                      <NeoGrid.Column
                        display={meta.isExcludedInd}
                        label={TextConstants.Labels.Excluded}
                      />
                      <NeoGrid.Column
                        display={meta.isReversalInd}
                        label={TextConstants.Labels.Reversal}
                      />
                    </NeoGrid.Row>
                  )}
                </NeoGrid.Grid>
              </div>
            </Neo.Card>
          </Neo.Card>
        </Neo.Loader>
      </>
    );
  }
}
